import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef } from "@angular/core";
import { WidgetService } from "@app/services";
import { TranslocoService } from "@ngneat/transloco";
import { LocationWithDefaultWidgetGetDto } from "clearline-api";

interface WidgetTileButton {
  name: string;
  text: string;
  icon: string;
  iconColor: string;
  tooltip?: string;
  classList?: string;
  visible: boolean;
}

type WidgetTileButtonType = "Settings" | "Demo" | "Preview" | "Delete";

@Component({
  selector: "app-widget-tile",
  templateUrl: "./widget-tile.component.html",
  styleUrls: ["./widget-tile.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetTileComponent {
  @Input() set widgetData(value: LocationWithDefaultWidgetGetDto) {
    if (!!value) {
      this.widget = value;
      this.init();
    }
  }
  @Input() isAdmin = false;

  @Output() settingClick = new EventEmitter<LocationWithDefaultWidgetGetDto>();
  @Output() demoClick = new EventEmitter<LocationWithDefaultWidgetGetDto>();
  @Output() previewClick = new EventEmitter<LocationWithDefaultWidgetGetDto>();
  @Output() deleteClick = new EventEmitter<LocationWithDefaultWidgetGetDto>();
  @Output() toggleClick = new EventEmitter<LocationWithDefaultWidgetGetDto>();

  protected isInteractive = true;
  protected isDisabled = false;
  protected isSettingsAllowed = false;
  protected isPreviewAllowed = false;
  protected disabledTooltip: string | TemplateRef<any>;
  protected tileIconSrc = "";
  protected hasWarning = false;
  protected warningMessage = "";
  protected buttons: Map<WidgetTileButtonType, WidgetTileButton> = new Map<WidgetTileButtonType, WidgetTileButton>();
  protected widget: LocationWithDefaultWidgetGetDto;

  constructor(private transloco: TranslocoService, private widgetSvc: WidgetService) {}

  onToggleWidgetState(): void {
    if (this.isInteractive) {
      const { locationWidgetSettings } = this.widget;
      this.toggleClick.emit({
        ...this.widget,
        locationWidgetSettings: {
          ...locationWidgetSettings,
          disabled: !this.isDisabled
        }
      });
    }
  }

  onWidgetTileButtonClick(name: WidgetTileButtonType): void {
    switch (name) {
      case "Settings":
        this.settingClick.emit(this.widget);
        break;
      case "Demo":
        this.demoClick.emit(this.widget);
        break;
      case "Delete":
        this.deleteClick.emit(this.widget);
        break;
      case "Preview":
        this.previewClick.emit(this.widget);
        break;
      default:
        break;
    }
  }

  private init(): void {
    const locationSettings = this.widget.locationWidgetSettings;
    const hasSettings = !!locationSettings?.widgetSettingsJson;
    const hasWidgetCampaign = this.widgetSvc.hasWidgetCampaign(locationSettings?.defaultWidgetSettingsId);
    const isConfigured = !!locationSettings?.configured;

    this.isDisabled = this.widget.locationWidgetSettings?.disabled;
    this.isInteractive = this.widgetSvc.isInteractiveWidget(this.widget);
    this.hasWarning = (!hasSettings && hasWidgetCampaign) || !isConfigured;
    this.tileIconSrc = this.widget.icon;

    if (this.hasWarning) {
      this.warningMessage = this.getWarningMsg(this.widget);
    }

    this.initButtons();
  }

  private getWarningMsg(widget: LocationWithDefaultWidgetGetDto) {
    const widgetId = widget?.widget?.id;
    const widgetName = widget?.widget?.name;
    const locationSettings = this.widget.locationWidgetSettings;
    const hasSettings = !!locationSettings?.widgetSettingsJson;

    if (!hasSettings && this.widgetSvc.hasWidgetCampaign(widgetId)) {
      return "";
    }

    if (hasSettings && !locationSettings?.configured) {
      const i18nKey = this.isAdmin ? "widgets.common.pendingConfigAdmin" : "widgets.common.pendingConfig";

      return `${widgetName}${this.transloco.translate(i18nKey)}`;
    }
  }

  private hasPreviewButton(widgetDto: LocationWithDefaultWidgetGetDto): boolean {
    const { defaultWidgetSettings, locationWidgetSettings } = widgetDto;
    const featureId = defaultWidgetSettings?.id;

    return (
      !this.isDisabled &&
      this.widgetSvc.hasWidgetCampaign(featureId) &&
      !!locationWidgetSettings.widgetSettingsJson &&
      !!this.widgetSvc.getQrSettings(featureId, locationWidgetSettings)?.url
    );
  }

  private initButtons(): void {
    this.isSettingsAllowed = this.widgetSvc.isWidgetEditableByManager(this.widget.widget.id) && !this.isDisabled;
    this.isPreviewAllowed = this.hasPreviewButton(this.widget);

    this.buttons.set("Settings", {
      name: "Settings",
      text: this.transloco.translate("widgets.common.edit"),
      icon: "mdi mdi-cog",
      iconColor: "success",
      tooltip: this.transloco.translate("widgets.common.open"),
      classList: "cursor-pointer position-absolute configured font-size-20 bottom-0 end-0 mb-2",
      visible: this.isSettingsAllowed
    });
    this.buttons.set("Demo", {
      name: "Demo",
      text: this.transloco.translate("widgets.common.demo"),
      icon: "mdi mdi-eye",
      iconColor: "primary",
      classList: "cursor-pointer position-absolute review top-0 start-0 font-size-20",
      // button visibility is disabled since MAPP Settings were removed from Onboarding, keeping for future use
      visible: false && !this.isDisabled && (!!this.widget.description || !!this.widget.defaultWidgetSettings?.videoId)
    });
    this.buttons.set("Preview", {
      name: "Preview",
      text: this.transloco.translate("widgets.common.view"),
      icon: "mdi mdi-eye",
      iconColor: "primary",
      tooltip: this.transloco.translate("widgets.common.viewApp"),
      classList: "cursor-pointer position-absolute review top-0 start-0 font-size-20",
      visible: this.isPreviewAllowed
    });
    this.buttons.set("Delete", {
      name: "Delete",
      text: this.transloco.translate("widgets.common.delete"),
      icon: "mdi mdi-trash-can-outline",
      iconColor: "primary",
      tooltip: this.transloco.translate("widgets.common.deleteTooltip"),
      classList: "cursor-pointer position-absolute review bottom-0 start-0 font-size-20 mb-2",
      visible: this.widget?.defaultWidgetSettings?.id !== this.widget?.widget?.id
    });
  }
}
